import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Button, StatCard, Table, useTable } from "@repo/o37-ui";
import { Plus, Users, Mail, Phone, Building } from "lucide-react";
import { HoldingsPerson, formatCents } from '../../types';
import { HoldingsCompensation, transformCompensation } from '../../types/compensation';
import PersonForm from '../../components/Holdings/PersonForm';

// Extended person type with compensation
interface HeadcountPerson extends HoldingsPerson {
  compensation?: HoldingsCompensation;
}

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function PeopleList() {
  const [people, setPeople] = useState<HeadcountPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<HeadcountPerson | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadPeople();
  }, []);

  const loadPeople = async () => {
    try {
      // Fetch people and compensation separately (same approach as PersonForm detail modal)
      const [peopleRes, compRes] = await Promise.all([
        fetch(`${API_URL}/holdings/people`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/compensation`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (peopleRes.ok) {
        const peopleData = await peopleRes.json();
        const compData = compRes.ok ? await compRes.json() : [];

        // Create a map of person_id -> compensation for quick lookup
        const compByPersonId = new Map<string, any>();
        for (const comp of compData) {
          compByPersonId.set(comp.person_id, comp);
        }

        const transformed: HeadcountPerson[] = peopleData.map((p: any) => {
          // Get compensation from the separate fetch (same as PersonForm)
          const personComp = compByPersonId.get(p.id);
          let compensation = personComp ? transformCompensation(personComp) : undefined;

          // Add company info if available
          if (compensation && personComp?.holdings_companies) {
            compensation = {
              ...compensation,
              company: {
                id: personComp.holdings_companies.id,
                name: personComp.holdings_companies.name,
                status: personComp.holdings_companies.status,
                ownershipStakePercentage: undefined,
                createdAt: '',
                updatedAt: ''
              }
            };
          }

          return {
            id: p.id,
            name: p.name,
            email: p.email,
            phone: p.phone,
            role: p.role,
            notes: p.notes,
            isActive: p.is_active,
            createdBy: p.created_by,
            createdAt: p.created_at,
            updatedAt: p.updated_at,
            compensation
          };
        });
        // Sort: active first, then by name
        transformed.sort((a, b) => {
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return a.name.localeCompare(b.name);
        });
        setPeople(transformed);
      }
    } catch (e) {
      console.error('Failed to load people:', e);
    } finally {
      setLoading(false);
    }
  };

  const activePeople = people.filter(p => p.isActive);

  // Payroll calculations
  const totalAnnualPayroll = people
    .filter(p => p.compensation?.status === 'active')
    .reduce((sum, p) => sum + (p.compensation?.totalAnnualCostCents || 0), 0);
  const totalMonthlyPayroll = Math.round(totalAnnualPayroll / 12);
  const activeWithComp = people.filter(p => p.compensation?.status === 'active').length;
  const avgCompensation = activeWithComp > 0 ? Math.round(totalAnnualPayroll / activeWithComp) : 0;

  const columns = useMemo(() => [
    {
      id: "name",
      header: "Name",
      accessorFn: (row: HeadcountPerson) => row.name,
      cell: ({ row }: { row: { original: HeadcountPerson } }) => (
        <div className="py-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-neutral-900">
              {row.original.name}
            </span>
            {!row.original.isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-neutral-100 text-neutral-500">
                Inactive
              </span>
            )}
          </div>
          {row.original.role && (
            <div className="text-sm text-neutral-500">{row.original.role}</div>
          )}
        </div>
      )
    },
    {
      id: "contact",
      header: "Contact",
      cell: ({ row }: { row: { original: HeadcountPerson } }) => (
        <div className="space-y-1">
          {row.original.email && (
            <div className="flex items-center gap-1.5 text-sm">
              <Mail className="w-3.5 h-3.5 text-neutral-400" />
              <a
                href={`mailto:${row.original.email}`}
                className="text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {row.original.email}
              </a>
            </div>
          )}
          {row.original.phone && (
            <div className="flex items-center gap-1.5 text-sm text-neutral-500">
              <Phone className="w-3.5 h-3.5 text-neutral-400" />
              <span>{row.original.phone}</span>
            </div>
          )}
          {!row.original.email && !row.original.phone && (
            <span className="text-neutral-400 text-sm">-</span>
          )}
        </div>
      )
    },
    {
      id: "company",
      header: "Company",
      cell: ({ row }: { row: { original: HeadcountPerson } }) => {
        const comp = row.original.compensation;
        if (comp?.company?.name) {
          return (
            <span className="inline-flex items-center gap-1 text-sm text-neutral-700">
              <Building className="w-3.5 h-3.5 text-neutral-400" />
              {comp.company.name}
            </span>
          );
        }
        return <span className="text-neutral-400 text-sm">No compensation</span>;
      }
    },
    {
      id: "compensation",
      header: "Total Comp",
      cell: ({ row }: { row: { original: HeadcountPerson } }) => {
        const comp = row.original.compensation;
        if (!comp) {
          return <span className="text-neutral-400 text-sm">-</span>;
        }
        return (
          <div>
            <div className="font-semibold text-neutral-900">
              {formatCents(comp.totalAnnualCostCents)}
            </div>
            <div className="text-xs text-neutral-500">
              {formatCents(Math.round(comp.totalAnnualCostCents / 12))}/mo
            </div>
          </div>
        );
      }
    },
    {
      id: "actions",
      header: "",
      size: 100,
      meta: {
        headerClassName: "text-right",
        cellClassName: "justify-end"
      },
      cell: ({ row }: { row: { original: HeadcountPerson } }) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            className="h-8 text-xs px-3"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setEditingPerson(row.original);
              setShowForm(true);
            }}
            text="Edit"
          />
        </div>
      )
    }
  ], []);

  const { table, ...tableProps } = useTable({
    data: people,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: people.length,
    resourceName: (plural) => `${plural ? "people" : "person"}`,
  });

  const handleFormClose = () => {
    setShowForm(false);
    setEditingPerson(null);
  };

  const handleFormSaved = () => {
    handleFormClose();
    loadPeople();
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Headcount Directory</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Headcount Directory</h1>
          <p className="text-neutral-500">Manage headcount and compensation across your portfolio</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          icon={<Plus className="w-4 h-4" />}
          text="Add Person"
        />
      </div>

      {/* Summary Stats */}
      {people.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Active Headcount"
            value={activePeople.length.toString()}
            hint={`${people.length} total`}
          />
          <StatCard
            label="Monthly Payroll"
            value={formatCents(totalMonthlyPayroll)}
            hint={`${activeWithComp} with compensation`}
          />
          <StatCard
            label="Annual Payroll"
            value={formatCents(totalAnnualPayroll)}
            hint="Total cost"
          />
          <StatCard
            label="Avg Compensation"
            value={formatCents(avgCompensation)}
            hint="Per person/year"
          />
        </div>
      )}

      {/* Headcount Table */}
      {people.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Headcount Yet</h3>
            <p className="text-neutral-500 mb-6">Add your first team member to track headcount.</p>
            <Button
              onClick={() => setShowForm(true)}
              text="Add Person"
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table {...tableProps} table={table} />
        </Card>
      )}

      {/* Person Form Modal */}
      {showForm && (
        <PersonForm
          person={editingPerson}
          onClose={handleFormClose}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
